import React, { useState, useEffect } from 'react';
import { Modal, Table, Text, Loader, Box } from '@mantine/core';
import TestsApi from "../../api/TestsApi";

interface PredictiveStatusLogProps {
  opened: boolean;
  onClose: () => void;
  pageId: string;
  selectedMemberWebsite: string;
  goalType: string;
  goalId: string;
}

// Client names for the engine's states (Build 1 change 40: "Leader Emerging", never "Strong Signal")
const STATE_LABEL: Record<string, string> = {
  PREDICTED_WINNER: 'Predicted Winner',
  PROCEED_WITH_CAUTION: 'Leader Emerging',
  TRENDING: 'Trending',
  NO_PREDICTION: 'No Prediction',
};

export function PredictiveStatusLog({ opened, onClose, pageId, selectedMemberWebsite, goalType, goalId }: PredictiveStatusLogProps) {
  const [logData, setLogData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (opened && pageId) {
      setLoading(true);
      TestsApi.getpredictivestatuslog({
        system_website_id: selectedMemberWebsite,
        page_id: pageId,
        goal_type: goalType,
        goal_id: goalId,
      })
      .then(response => response.json())
      .then(result => {
        if (result.status === 1 && result.data) {
          setLogData(result.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    }
  }, [opened, pageId, selectedMemberWebsite, goalType, goalId]);

  const iconMap: Record<string, string> = {
    green: '\u{1F7E2}',
    yellow: '\u{1F7E1}',
    blue: '\u{1F535}',
    red: '\u{1F534}',
  };

  const rows = logData.map((entry, index) => (
    <tr key={index}>
      <td>{entry.day_key}</td>
      <td>
        <Text size="sm">
          {iconMap[entry.icon] || '\u{1F534}'} {STATE_LABEL[entry.state] || entry.state}
        </Text>
        {entry.note && (
          <Text size="xs" mt={2} color="dimmed">{entry.note}</Text>
        )}
      </td>
      <td>{entry.leading_variation || '—'}</td>
      <td>
        {entry.pause_recs
          ? <Text
              size="sm"
              sx={(theme) => ({
                color: theme.colorScheme === 'dark' ? theme.colors.orange[4] : theme.colors.orange[8],
              })}
            >
              {'\u{1F7E0} '}{entry.pause_recs}
            </Text>
          : <Text size="sm" color="dimmed">—</Text>}
      </td>
    </tr>
  ));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Predictive Status Log"
      size="xl"
    >
      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <Loader size="sm" />
        </Box>
      ) : logData.length === 0 ? (
        <Text size="sm" color="dimmed" ta="center" py={20}>
          No predictive status data available yet.
        </Text>
      ) : (
        <Table striped highlightOnHover withBorder fontSize="xs">
          <thead>
            <tr>
              <th>Date</th>
              <th>State</th>
              <th>Leading Variation</th>
              <th>Pause Recommendations</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
    </Modal>
  );
}
